import React, { useState, useEffect } from "react";
import "./UpdateAirlineCountDialog.css";
import { Button, CircularProgress } from "@mui/material";
import SnackbarAlert from "../../../../../components/element/SnackbarAlert";
import { useConfiguration } from "../../../../../services/ConfigurationProvider";
import {
  getDiplomatsPassenger,
  getGovernmentPassenger,
  getMilitaryPassenger,
  getInfantPassenger,
} from "../../../../../utils/configUtils";

const UpdateAirlineCountDialog = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  isLoading,
}) => {
  const { configurationData } = useConfiguration();

  const governmentPassengerEnabled = getGovernmentPassenger(configurationData);
  const diplomatsPassengerEnabled = getDiplomatsPassenger(configurationData);
  const militaryPassengerEnabled = getMilitaryPassenger(configurationData);
  const infantPassengerEnabled = getInfantPassenger(configurationData);

  const [passengerCounts, setPassengerCounts] = useState({
    bmsPassengerCount: initialData?.scannedPassengerCount || 0,
    totalBoarded: initialData?.airlinePassengerCount || 0,
    totalExempted: initialData?.airlineExemptedPassengerCount || 0,
    infant: initialData?.airlineInfant || 0,
    military: initialData?.airlineMilitary || 0,
    government: initialData?.airlineGovernment || 0,
    diplomat: initialData?.airlineDiplomats || 0,
  });

  const [error, setError] = useState("");
  const [errorKey, setErrorKey] = useState(0);

  // Update state when initialData changes (from API)
  useEffect(() => {
    setPassengerCounts({
      bmsPassengerCount: initialData?.scannedPassengerCount || 0,
      totalBoarded: initialData?.airlinePassengerCount || 0,
      totalExempted: initialData?.airlineExemptedPassengerCount || 0,
      infant: initialData?.airlineInfant || 0,
      military: initialData?.airlineMilitary || 0,
      government: initialData?.airlineGovernment || 0,
      diplomat: initialData?.airlineDiplomats || 0,
    });
  }, [initialData]);

  // Calculate total exempted passengers whenever the four categories change
  useEffect(() => {
    let totalExempted = 0;
    if (infantPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.infant);
    }

    // Only include military if enabled
    if (militaryPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.military);
    }
    // Only include government if enabled
    if (governmentPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.government);
    }
    // Only include diplomat if enabled
    if (diplomatsPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.diplomat);
    }
    setPassengerCounts((prev) => ({
      ...prev,
      totalExempted,
    }));
  }, [
    passengerCounts.infant,
    passengerCounts.military,
    passengerCounts.government,
    passengerCounts.diplomat,
    infantPassengerEnabled,
    militaryPassengerEnabled,
    governmentPassengerEnabled,
    diplomatsPassengerEnabled,
  ]);

  // Convert empty strings to 0 for calculation purposes
  const getNumericValue = (value) => {
    return value === "" ? 0 : value || 0;
  };

  // Validation function to check if totalBoarded >= totalExempted (allows 0)
  const validatePassengerCounts = (totalBoarded, totalExempted) => {
    const boarded = getNumericValue(totalBoarded);
    const exempted = getNumericValue(totalExempted);
    if (boarded < exempted) {
      return false;
    } else {
      return true;
    }
  };

  const handleInputChange = (field, value) => {
    let numValue;

    if (value === null || value === undefined || value === "") {
      numValue = "";
    } else {
      const parsed = parseInt(value);
      if (isNaN(parsed)) {
        numValue = "";
      } else {
        // Prevent negative numbers by setting them to 0
        numValue = parsed < 0 ? 0 : parsed;
      }
    }
    setPassengerCounts((prev) => ({
      ...prev,
      [field]: numValue,
    }));
  };

  const handleSave = () => {
    // Convert empty strings to 0 before sending to API
    const dataToSave = {
      totalBoarded:
        passengerCounts.totalBoarded === "" ? 0 : passengerCounts.totalBoarded,
      totalExempted:
        passengerCounts.totalExempted === ""
          ? 0
          : passengerCounts.totalExempted,
      infant: passengerCounts.infant === "" ? 0 : passengerCounts.infant,
      military: passengerCounts.military === "" ? 0 : passengerCounts.military,
      government:
        passengerCounts.government === "" ? 0 : passengerCounts.government,
      diplomat: passengerCounts.diplomat === "" ? 0 : passengerCounts.diplomat,
    };

    // Validate before saving
    const isValid = validatePassengerCounts(
      dataToSave.totalBoarded,
      dataToSave.totalExempted
    );
    if (isValid) {
      setError("");
      setErrorKey(0);
      onSave(dataToSave);
    } else {
      setError(
        "Total boarded passengers must be greater than or equal to total exempted passengers"
      );
      setErrorKey((prev) => prev + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="update-airline-count-modal-overlay">
      <div className="update-airline-count-modal-content">
        <div className="update-airline-count-modal-header">
          <h2 className="update-airline-count-modal-title">
            Passenger(s) Count
          </h2>
          <button
            className="update-airline-count-modal-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="update-airline-count-form">
          {/* <div className="update-airline-count-input-group">
            <label htmlFor="bmsPassengerCount">BMS Passenger(s) Count</label>
            <input
              id="bmsPassengerCount"
              type="number"
              value={passengerCounts.bmsPassengerCount}
              className="update-airline-count-input disabled"
              disabled
            />
          </div> */}

          <div className="update-airline-count-input-group">
            <label htmlFor="totalBoarded">Total Boarded Passengers (including exemptions)</label>
            <input
              id="totalBoarded"
              type="number"
              value={passengerCounts.totalBoarded}
              onChange={(e) =>
                handleInputChange("totalBoarded", e.target.value)
              }
              className="update-airline-count-input active"
            />
          </div>

          <div className="update-airline-count-input-group">
            <label htmlFor="totalExempted">Total Exempted Passengers</label>
            <input
              id="totalExempted"
              type="number"
              value={passengerCounts.totalExempted}
              className="update-airline-count-input disabled"
              disabled
            />
          </div>

          {infantPassengerEnabled && (
            <div className="update-airline-count-input-group">
              <label htmlFor="infant">Infant</label>
              <input
                id="infant"
                type="number"
                value={passengerCounts.infant}
                onChange={(e) => handleInputChange("infant", e.target.value)}
                className="update-airline-count-input"
              />
            </div>
          )}

          {militaryPassengerEnabled && (
            <div className="update-airline-count-input-group">
              <label htmlFor="military">Military</label>
              <input
                id="military"
                type="number"
                value={passengerCounts.military}
                onChange={(e) => handleInputChange("military", e.target.value)}
                className="update-airline-count-input"
              />
            </div>
          )}

          {governmentPassengerEnabled && (
            <div className="update-airline-count-input-group">
              <label htmlFor="government">Government</label>
              <input
                id="government"
                type="number"
                value={passengerCounts.government}
                onChange={(e) =>
                  handleInputChange("government", e.target.value)
                }
                className="update-airline-count-input"
              />
            </div>
          )}

          {diplomatsPassengerEnabled && (
            <div className="update-airline-count-input-group">
              <label htmlFor="diplomat">Diplomat</label>
              <input
                id="diplomat"
                type="number"
                value={passengerCounts.diplomat}
                onChange={(e) => handleInputChange("diplomat", e.target.value)}
                className="update-airline-count-input"
              />
            </div>
          )}
        </div>

        {error && (
          <SnackbarAlert key={errorKey} type="error">
            {error}
          </SnackbarAlert>
        )}

        <Button
          className="update-airline-count-save-button"
          variant="contained"
          onClick={!isLoading ? handleSave : null}
          endIcon={
            isLoading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default UpdateAirlineCountDialog;
